/**
 * The schedule editor's draft, and everything that turns it into an API body
 * or checks it. The checks mirror `DiscountScheduleSerializer.validate` in
 * the backend (vendor/discount_api.py); the server stays the judge.
 *
 * Units: rule amounts are MENU UNITS, the same numbers the menu screen's
 * price field takes (`VendorEventItem.price`): whole tokens on a token
 * event; on other events the currency value is menu units × `token_value`
 * (vendor/pricing.py), and the menu screen shows that product beside the
 * field without converting what is typed. Percent is typed as a percent
 * (15) and sent as a fraction (0.15).
 */
import type {
  DiscountItemMode,
  DiscountPreviewRow,
  DiscountRuleType,
  DiscountSchedule,
  DiscountScheduleInput,
  DiscountScheduleItemLink,
  DiscountWindowType,
} from '@/lib/api/discount-types'
import { collectMessages } from '@/lib/api/discount-client'
import type { MenuItem, VendorEvent } from '@/lib/api/types'
import { instantToEventLocal } from '@/lib/eventTime'
import { wallClockToInstant } from './event-clock'
import { floorToCent, formatCurrency, formatMenuPrice, type MenuUnits } from './money'

export { formatCurrency, formatMenuPrice, menuUnits, type MenuUnits } from './money'

// ---------------------------------------------------------------- units

/**
 * What an amount typed in menu units is worth in currency, when that differs
 * from what was typed. A set price is rounded down to the cent, as the
 * backend charges it (`apply_rule`); pass `ruleType` 'tokens_off' for an
 * amount off, which rounds to the nearest cent.
 */
export function currencyEquivalent(value: number, units: MenuUnits, ruleType?: DiscountRuleType): string | null {
  if (!units.tokenValue) return null
  if (units.eventType === 'direct_pay' && units.tokenValue === 1) return null
  const amount = value * units.tokenValue
  return formatCurrency(ruleType === 'tokens_off' ? amount : floorToCent(amount), units.currency)
}

/** The unit an amount field takes, as a short suffix. */
export function amountUnitLabel(units: MenuUnits): string {
  if (units.eventType !== 'direct_pay') return 'tokens'
  return units.tokenValue === 1 ? units.currency : 'menu units'
}

// ---------------------------------------------------------------- decimals

export interface ParsedDecimal {
  /** Canonical text: no leading or trailing zeros ("2.50" → "2.5"). */
  text: string
  value: number
  /** Decimal places that matter (trailing zeros dropped). */
  places: number
  /** Digits before the point, leading zeros dropped. */
  wholeDigits: number
}

/** A non-negative decimal as typed ("2", "2.50", ".5"), or null when it isn't one. */
export function parseDecimal(raw: string): ParsedDecimal | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(raw.trim())
  if (!match || (!match[1] && !match[2])) return null
  const whole = (match[1] || '0').replace(/^0+(?=\d)/, '')
  const fraction = (match[2] || '').replace(/0+$/, '')
  const text = fraction ? `${whole}.${fraction}` : whole
  return { text, value: Number(text), places: fraction.length, wholeDigits: whole === '0' ? 0 : whole.length }
}

function trimZeros(text: string): string {
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text
}

/** "12.5" (percent, at most two places) → "0.125" (the fraction the API stores at four places). */
export function percentToFraction(percent: ParsedDecimal): string {
  return trimZeros((Math.round(percent.value * 100) / 10000).toFixed(4))
}

/** "0.1250" or 0.125 (fraction) → "12.5" (percent). */
export function fractionToPercent(fraction: string | number): string {
  const value = Number(fraction)
  if (!Number.isFinite(value)) return ''
  return trimZeros((Math.round(value * 10000) / 100).toFixed(2))
}

/** "4.0000" or 4 → "4"; "2.5000" → "2.5". */
function amountToInput(amount: string | number): string {
  const value = Number(amount)
  return Number.isFinite(value) ? trimZeros(value.toFixed(4)) : String(amount)
}

// ---------------------------------------------------------------- draft

export interface RuleDraft {
  type: DiscountRuleType | ''
  /** As typed: menu units, or a percent (15) for percent_off. */
  value: string
}

export interface ItemDraft {
  item: number
  mode: DiscountItemMode
  /** Used only when `mode` is override. */
  rule: RuleDraft
}

export interface ScheduleDraft {
  name: string
  isActive: boolean
  defaultRule: RuleDraft
  vendors: number[]
  /** VendorEvent ids. */
  locations: number[]
  items: ItemDraft[]
  windowType: DiscountWindowType
  /** one_off: event-local "yyyy-MM-ddTHH:mm"; '' = open-ended. */
  startsAt: string
  endsAt: string
  /** recurring: 0 = Monday … 6 = Sunday; empty = every day. */
  days: number[]
  /** recurring: "HH:MM" in the event timezone. */
  dailyStart: string
  dailyEnd: string
  /** recurring: event-local "YYYY-MM-DD"; '' = none. */
  startDate: string
  endDate: string
}

/**
 * A change to some of the draft's fields: the new values, or a function of
 * the current draft (for toggles, so two quick changes don't read the same
 * stale draft).
 */
export type DraftUpdate<K extends keyof ScheduleDraft = keyof ScheduleDraft> =
  | Partial<Pick<ScheduleDraft, K>>
  | ((draft: ScheduleDraft) => Partial<Pick<ScheduleDraft, K>>)

/** The list with `id` added, or removed when it was there. */
export function toggleId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id]
}

export const EMPTY_RULE: RuleDraft = { type: '', value: '' }

export const RULE_LABEL: Record<DiscountRuleType, string> = {
  set_price: 'Set price',
  tokens_off: 'Amount off',
  percent_off: 'Percent off',
}

function ruleFromApi(type: DiscountRuleType | null, value: string | number | null): RuleDraft {
  if (!type) return EMPTY_RULE
  if (value === null || value === '') return { type, value: '' }
  return { type, value: type === 'percent_off' ? fractionToPercent(value) : amountToInput(value) }
}

export function newDraft(units: MenuUnits): ScheduleDraft {
  return {
    name: '',
    isActive: true,
    defaultRule: { type: units.tokenEvent ? 'tokens_off' : 'percent_off', value: '' },
    vendors: [],
    locations: [],
    items: [],
    windowType: 'one_off',
    startsAt: '',
    endsAt: '',
    days: [],
    dailyStart: '',
    dailyEnd: '',
    startDate: '',
    endDate: '',
  }
}

export function scheduleToDraft(schedule: DiscountSchedule, timeZone: string): ScheduleDraft {
  return {
    name: schedule.name,
    isActive: schedule.is_active,
    defaultRule: ruleFromApi(schedule.default_rule_type, schedule.default_rule_value),
    vendors: [...schedule.vendors],
    locations: [...schedule.locations],
    items: schedule.items.map((link) => ({
      item: link.item,
      mode: link.mode,
      rule: link.mode === 'override' ? ruleFromApi(link.rule_type, link.rule_value) : EMPTY_RULE,
    })),
    windowType: schedule.window_type,
    startsAt: instantToEventLocal(schedule.starts_at, timeZone),
    endsAt: instantToEventLocal(schedule.ends_at, timeZone),
    days: [...(schedule.days_of_week ?? [])].sort((a, b) => a - b),
    dailyStart: (schedule.daily_start_time ?? '').slice(0, 5),
    dailyEnd: (schedule.daily_end_time ?? '').slice(0, 5),
    startDate: schedule.start_date ?? '',
    endDate: schedule.end_date ?? '',
  }
}

// ---------------------------------------------------------------- API body

function ruleValueForApi(rule: RuleDraft): string | null {
  if (!rule.type) return null
  const parsed = parseDecimal(rule.value)
  if (!parsed) return rule.value.trim() || null
  return rule.type === 'percent_off' ? percentToFraction(parsed) : parsed.text
}

/**
 * An event-local "yyyy-MM-ddTHH:mm" as an ISO instant, or null when blank or
 * unreadable. DST follows the backend (see `./event-clock`).
 */
export function localToInstant(local: string, timeZone: string): string | null {
  if (!local) return null
  const [date = '', time = ''] = local.split('T')
  const instant = wallClockToInstant(date, time, timeZone)
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString()
}

/**
 * The whole draft as a create (or preview) body. The fields of the window
 * type not chosen go as null / [] so a switch clears what the other type
 * left behind.
 */
export function draftToInput(draft: ScheduleDraft, eventId: number, timeZone: string): DiscountScheduleInput {
  const oneOff = draft.windowType === 'one_off'
  return {
    event: eventId,
    name: draft.name.trim(),
    is_active: draft.isActive,
    default_rule_type: draft.defaultRule.type || null,
    default_rule_value: ruleValueForApi(draft.defaultRule),
    window_type: draft.windowType,
    starts_at: oneOff ? localToInstant(draft.startsAt, timeZone) : null,
    ends_at: oneOff ? localToInstant(draft.endsAt, timeZone) : null,
    days_of_week: oneOff ? [] : [...draft.days].sort((a, b) => a - b),
    daily_start_time: oneOff ? null : draft.dailyStart || null,
    daily_end_time: oneOff ? null : draft.dailyEnd || null,
    start_date: oneOff ? null : draft.startDate || null,
    end_date: oneOff ? null : draft.endDate || null,
    vendors: [...draft.vendors],
    locations: [...draft.locations],
    items: draft.items.map((row): DiscountScheduleItemLink => ({
      item: row.item,
      mode: row.mode,
      rule_type: row.mode === 'override' ? row.rule.type || null : null,
      rule_value: row.mode === 'override' ? ruleValueForApi(row.rule) : null,
    })),
  }
}

const SCALAR_KEYS = [
  'name', 'is_active', 'window_type', 'starts_at', 'ends_at', 'days_of_week',
  'daily_start_time', 'daily_end_time', 'start_date', 'end_date',
] as const

function targetsSignature(input: DiscountScheduleInput): string {
  const byNumber = (a: number, b: number) => a - b
  return JSON.stringify([
    [...(input.vendors ?? [])].sort(byNumber),
    [...(input.locations ?? [])].sort(byNumber),
    [...(input.items ?? [])].sort((a, b) => a.item - b.item).map((row) => [row.item, row.mode, row.rule_type, row.rule_value]),
  ])
}

/**
 * The PATCH body: only what changed. The default rule's type and value go
 * together. `vendors`, `locations` and `items` replace the stored lists
 * when present, so when any of them changed all three go in full.
 */
export function changedFields(before: DiscountScheduleInput, after: DiscountScheduleInput): Partial<DiscountScheduleInput> {
  const changes: Partial<DiscountScheduleInput> = {}
  const record = changes as Record<string, unknown>
  for (const key of SCALAR_KEYS) {
    if (JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null)) record[key] = after[key]
  }
  if (before.default_rule_type !== after.default_rule_type || before.default_rule_value !== after.default_rule_value) {
    changes.default_rule_type = after.default_rule_type
    changes.default_rule_value = after.default_rule_value
  }
  if (targetsSignature(before) !== targetsSignature(after)) {
    changes.vendors = after.vendors
    changes.locations = after.locations
    changes.items = after.items
  }
  return changes
}

// ---------------------------------------------------------------- checks

export interface DraftErrors {
  name?: string
  defaultRule?: string
  targets?: string
  /** Own-rule problems, by item id. */
  items: Record<number, string>
  endsAt?: string
  dailyTimes?: string
  endDate?: string
}

export interface CheckOptions {
  units: MenuUnits
  timeZone: string
  /** Item id → list price, for the set-price ceiling. */
  listPrices: Map<number, number>
  requireName: boolean
  /** On an edit the server checks rule values only when they are sent. */
  checkDefaultRule: boolean
  checkItemRules: boolean
}

/** Mirrors `_check_rule` / `_check_amount_precision`, plus the set-price ceiling for an item. */
export function ruleError(rule: RuleDraft, units: MenuUnits, listPrice?: number): string | null {
  if (!rule.type) return 'Choose a rule.'
  const raw = rule.value.trim()
  if (!raw) return rule.type === 'percent_off' ? 'Enter a percent.' : 'Enter an amount.'
  if (raw.startsWith('-')) return 'Cannot be negative.'
  const parsed = parseDecimal(raw)
  if (!parsed) return 'Enter a number.'
  if (rule.type === 'percent_off') {
    if (units.tokenEvent) return 'Percent off is not available on token events.'
    if (parsed.value <= 0 || parsed.value > 100) return 'Enter a percent above 0 and at most 100.'
    if (parsed.places > 2) return 'Use at most two decimal places.'
    return null
  }
  if (rule.type === 'tokens_off' && parsed.value <= 0) return 'Amount off must be greater than 0.'
  if (units.tokenEvent && parsed.places > 0) return 'Token events use whole tokens.'
  // Hundredths of a menu unit are whole cents only when a unit is worth 1.
  if (!units.tokenEvent && parsed.places > 2) {
    return units.tokenValue === 1 ? 'Use at most two decimal places (whole cents).' : 'Use at most two decimal places.'
  }
  // DecimalField(max_digits=10, decimal_places=4) leaves six whole digits.
  if (parsed.wholeDigits > 6) return 'That amount is too large.'
  if (rule.type === 'set_price' && listPrice !== undefined && parsed.value > listPrice) {
    return `Above the list price (${formatMenuPrice(listPrice, units)}).`
  }
  return null
}

/** The default rule may be left empty only when every item has its own rule and nothing else is targeted. */
export function overridesOnly(draft: ScheduleDraft): boolean {
  return draft.items.length > 0
    && draft.items.every((row) => row.mode === 'override')
    && draft.vendors.length === 0
    && draft.locations.length === 0
}

export function validateDraft(draft: ScheduleDraft, options: CheckOptions): DraftErrors {
  const errors: DraftErrors = { items: {} }
  const name = draft.name.trim()
  if (options.requireName && !name) errors.name = 'Give the schedule a name.'
  else if (name.length > 100) errors.name = 'Keep the name to 100 characters.'

  if (!draft.defaultRule.type) {
    if (!overridesOnly(draft)) {
      errors.defaultRule = 'A default rule is needed unless every chosen item has its own rule and no vendor or location is chosen.'
    }
  } else if (options.checkDefaultRule) {
    const message = ruleError(draft.defaultRule, options.units)
    if (message) errors.defaultRule = message
  }

  if (!draft.vendors.length && !draft.locations.length && !draft.items.some((row) => row.mode !== 'exclude')) {
    errors.targets = 'Choose at least one vendor, location, or item to discount.'
  }
  if (options.checkItemRules) {
    for (const row of draft.items) {
      if (row.mode !== 'override') continue
      const message = ruleError(row.rule, options.units, options.listPrices.get(row.item))
      if (message) errors.items[row.item] = message
    }
  }

  if (draft.windowType === 'one_off') {
    const start = localToInstant(draft.startsAt, options.timeZone)
    const end = localToInstant(draft.endsAt, options.timeZone)
    if (start && end && Date.parse(end) <= Date.parse(start)) errors.endsAt = 'The end must be after the start.'
  } else {
    if (!draft.dailyStart || !draft.dailyEnd) errors.dailyTimes = 'Enter a daily start and end time.'
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
      errors.endDate = 'The end date cannot be before the start date.'
    }
  }
  return errors
}

export function hasErrors(errors: DraftErrors): boolean {
  return Object.entries(errors).some(([key, value]) => (key === 'items' ? Object.keys(value as object).length > 0 : !!value))
}

// ---------------------------------------------------------------- targets

export function itemLocationId(item: MenuItem): number | null {
  const location = item.vendor_event
  if (location && typeof location === 'object') return location.id
  return typeof location === 'number' ? location : null
}

export function locationVendor(location: VendorEvent): { id: number; name: string } | null {
  const vendor = location.vendor
  if (vendor && typeof vendor === 'object') return { id: vendor.id, name: vendor.name }
  return typeof vendor === 'number' ? { id: vendor, name: `Vendor ${vendor}` } : null
}

/** "Vendor · booth" (or the one of them there is). */
export function locationLabel(location: VendorEvent): string {
  const vendorName = locationVendor(location)?.name ?? ''
  const place = location.booth || location.location_name || location.name || ''
  if (vendorName && place && vendorName !== place) return `${vendorName} · ${place}`
  return vendorName || place || `Location ${location.id}`
}

/**
 * The rule the draft applies to an item, or null when it doesn't price it.
 * Mirrors `item_is_covered` in vendor/pricing.py: an exclude row wins over a
 * vendor or location, an override uses its own rule, anything else covered
 * takes the default.
 */
export function ruleForItem(
  draft: ScheduleDraft,
  item: MenuItem,
  rows: Map<number, ItemDraft>,
  vendorOfLocation: Map<number, number>,
): RuleDraft | null {
  const row = rows.get(item.id)
  if (row?.mode === 'exclude') return null
  if (row?.mode === 'override') return row.rule.type ? row.rule : null
  const location = itemLocationId(item)
  const vendor = location == null ? undefined : vendorOfLocation.get(location)
  const covered = row?.mode === 'include'
    || (location != null && draft.locations.includes(location))
    || (vendor !== undefined && draft.vendors.includes(vendor))
  if (!covered || !draft.defaultRule.type) return null
  return draft.defaultRule
}

function makesFree(rule: RuleDraft, listPrice: number, units: MenuUnits): boolean {
  if (!(listPrice > 0)) return false
  const parsed = parseDecimal(rule.value)
  if (!parsed) return false
  if (rule.type === 'set_price') return parsed.value === 0
  if (rule.type === 'tokens_off') return parsed.value >= listPrice
  if (rule.type === 'percent_off') return !units.tokenEvent && parsed.value >= 100
  return false
}

/**
 * Items the draft would make free: by its own rules (set price 0, an amount
 * off at or above the list price, 100 % off), or by the preview. An item
 * that is free at list price, or made free by another schedule, is not
 * counted.
 */
export function freeItemIds(
  draft: ScheduleDraft,
  items: MenuItem[],
  vendorOfLocation: Map<number, number>,
  units: MenuUnits,
  previewRows: DiscountPreviewRow[] | null | undefined,
): number[] {
  const rows = new Map(draft.items.map((row) => [row.item, row]))
  const free = new Set<number>()
  for (const item of items) {
    const rule = ruleForItem(draft, item, rows, vendorOfLocation)
    if (rule && makesFree(rule, item.price, units)) free.add(item.id)
  }
  for (const row of previewRows ?? []) {
    if (row.free && row.list_price > 0 && (row.wins || row.schedule_price === 0)) free.add(row.id)
  }
  return [...free]
}

// ---------------------------------------------------------------- server errors

export type ErrorSlot = 'name' | 'defaultRule' | 'targets' | 'window' | 'endsAt' | 'days' | 'dailyTimes' | 'endDate'

const FIELD_SLOT: Record<string, ErrorSlot> = {
  name: 'name',
  default_rule_type: 'defaultRule',
  default_rule_value: 'defaultRule',
  vendors: 'targets',
  locations: 'targets',
  items: 'targets',
  window_type: 'window',
  starts_at: 'endsAt',
  ends_at: 'endsAt',
  days_of_week: 'days',
  daily_start_time: 'dailyTimes',
  daily_end_time: 'dailyTimes',
  start_date: 'endDate',
  end_date: 'endDate',
}

export interface ServerErrors {
  fields: Partial<Record<ErrorSlot, string>>
  general: string | null
}

/**
 * Sorts a failed save's 400 body onto the form: a key that names a field
 * goes beside it, anything else (non_field_errors, a refusal's detail) goes
 * to the top. "Item 12" in a message becomes the item's name.
 */
export function mapServerErrors(
  error: unknown,
  itemName: (id: number) => string | null,
  fallback: (error: unknown) => string,
): ServerErrors {
  const nameItems = (text: string) =>
    text.replace(/\b([Ii]tem) (\d+)\b/g, (match, _word: string, id: string) => {
      const name = itemName(Number(id))
      return name ? `“${name}”` : match
    })
  const data = (error as { data?: unknown } | null)?.data
  const result: ServerErrors = { fields: {}, general: null }
  if (!data || typeof data !== 'object' || Array.isArray(data) || 'detail' in data) {
    result.general = nameItems(fallback(error))
    return result
  }
  const general: string[] = []
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const text = collectMessages(value).map(nameItems).join(' ')
    if (!text) continue
    const slot = FIELD_SLOT[key]
    if (slot) result.fields[slot] = result.fields[slot] ? `${result.fields[slot]} ${text}` : text
    else general.push(text)
  }
  if (general.length) result.general = general.join(' ')
  else if (!Object.keys(result.fields).length) result.general = nameItems(fallback(error))
  return result
}
